import React, { useState } from 'react'
import { Box, Text, Flex, Button, Divider, Stack, HStack, useDisclosure } from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import { moneyFormat } from 'utils/functions/formatNumber'
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons'
import { useOfferingProvider } from 'providers/offeringProvider/OfferingProvider'
import IBModalAction from 'components/common/IBModalAction/IBModalAction'
import { IAdditionalServiceVarSelected } from 'providers/offeringProvider/IOfferingProvider.d'

interface SummaryPlanProps {
  step: 'accounts' | 'additionalServices' | 'summary' | 'summaryFinal';
  resetValueServicesAditional?: () => void;
}

const SummaryPlan: React.FC<SummaryPlanProps> = ({ step, resetValueServicesAditional }) => {
  const navigate = useNavigate()
  const { selectedOfferings } = useOfferingProvider()
  const {
    plan: {
      denomination: planName,
      price: planPrice,
      transferAmountLimitArs: planLimitsArs,
      transferAmountLimitUsd: planLimitsUsd,
      transferQuantityLimit: planTransferencia,
      consultationLimit: planConsultas,
    },
    additionalServices,
  } = selectedOfferings || {}
  const isStepAdditionalServcice: boolean = step === 'additionalServices'
  const isStepSummary: boolean = step === 'summary'
  const isSummaryFinal: boolean = step === 'summaryFinal'
  const text = {
    chosePlan: 'Elegiste el plan',
    byMonth: 'Por mes podrás',
    transfer: 'Transferir en pesos',
    transferUsd: 'Transferir en dólares',
    send: 'Enviar',
    tx: 'transferencias',
    reports: 'reportes',
    download: 'Descargar',
    change: 'Cambiar',
    hideDetail: 'Ocultar detalle',
    showDetail: 'Ver detalle',
    additionalService: 'Servicios adicionales',
    plan: 'Plan',
    conditionPrice: 'Precio variable sujeto a condiciones del servicio',
    since: 'Desde',
    unlimited: 'Ilimitado'
  }
  const additionalServicesCount = additionalServices?.length || 0
  const hasVariableService = additionalServices?.some(service => service.type === 'variable')
  const priceByTitle = hasVariableService ? 'Precio inicial' : 'Precio final'
  const totalAdditionalServicesPrice = additionalServices?.reduce((total, service) => total + service.price, 0)
  const totalPrice = moneyFormat((planPrice || 0) + totalAdditionalServicesPrice, 'ARS')

  const [isExpanded, setIsExpanded] = useState(false)
  const [showDetails, setShowDetails] = useState(false)
  const [selectedService, setSelectedService] = useState<IAdditionalServiceVarSelected>({ note: null, denomination: null, codeDn:null})
  const { isOpen: isOpenModalPrice, onOpen: onOpenModalPrice, onClose: onCloseModalPrice } = useDisclosure()
  
  const toggleExpansion = () => {
    setIsExpanded(!isExpanded)
  }

  const toggleExpansionService = () => {
    setShowDetails(!showDetails)
  }

  const handleButton = () => {
    resetValueServicesAditional && resetValueServicesAditional()
    navigate('/planes')
  }

  const handleAdditionalServcice = () => {
    navigate('/servicios-adicionales')
  }

  const openModalWithNote = (note: string, denomination: string, codeDn: string): void => {
    const serviceVar: IAdditionalServiceVarSelected = { note, denomination, codeDn }
    setSelectedService(serviceVar)
    onOpenModalPrice()
  }

  const getTitle = (): string => {
    if (selectedService.codeDn && selectedService.codeDn.toLocaleLowerCase() === 'trd') {
      return selectedService.denomination
    }
    return selectedService.denomination ? selectedService.denomination.toLowerCase() : ''
  }
  return (
      <Box
        data-testid='modal-summary'
        as='section'
        ml={8}
        borderRadius='8px'
        border='1px solid'
        borderColor='secondary.60'
        background='white'
      >
        <Flex dir='column' borderTopRadius='xl' p={7} >
          <Box dir='column' flex={0.7} >
            <Text textStyle='labelMd' color='grey.100'>{text.chosePlan}</Text>
            <Text textStyle='h4Bold' color='tertiary.500' >{planName}</Text>
            {!isExpanded ?
              <Box mt={3} color='primary.500' pb={2}>
                <Button data-testid='viewPlanDetail' textStyle='labelSmBoldUnderline' variant='primary-text' p={0} onClick={toggleExpansion}>{text.showDetail} <ChevronDownIcon color='primary.500' /></Button>
              </Box>
              :
              <Box mt={5} >
                <Text color='tertiary.500' textStyle='labelMdBold' >{text.byMonth}</Text>
              </Box>
            }
          </Box>
          <Flex dir='row' flex={0.3} justifyContent='flex-end' textStyle='labelMdBold' color='primary.500'>
            <Button 
              data-testid='changePlan-button'
              w='79px' 
              variant='primary-outline' 
              color='primary.500' 
              onClick={handleButton}
              disabled={isSummaryFinal}
              >
              <Text textStyle='labelLgBold' p={1}>{text.change}</Text>
            </Button>
          </Flex>
        </Flex>
        {isExpanded && (
          <Box p={7} pt={0}>
            <Box dir='column' mb={2}>
              <Text color='grey.100' textStyle='labelSm' >{text.transfer}</Text>
              <Text textStyle='h6' color='grey.120'>{planLimitsArs === -1 ? text.unlimited : moneyFormat(planLimitsArs, 'ARS')}</Text>
            </Box>
            <Box dir='column' mb={2}>
              <Text color='grey.100' textStyle='labelSm' >{text.transferUsd}</Text>
              <Text textStyle='h6' color='grey.120'>{planLimitsUsd === -1 ? text.unlimited : moneyFormat(planLimitsUsd, 'USD')}</Text>
            </Box>
            <Box dir='column' mb={2}>
              <Text color='grey.100' textStyle='labelSm' >{text.send}</Text>
              <Text textStyle='h6' color='grey.120'>{planTransferencia === -1 ? text.unlimited : `${planTransferencia} ${text.tx}`}</Text>
            </Box>
            <Box dir='column' mb={2}>
              <Text color='grey.100' textStyle='labelSm' >{text.download}</Text>
              <Text textStyle='h6' color='grey.120'>{planConsultas === -1 ? text.unlimited : `${planConsultas} ${text.reports}`}</Text>
            </Box>
            <Box mt={5} color='primary.500'>
              <Button data-testid='hideDetailPlan' textStyle='labelSmBoldUnderline' variant='primary-text' p={0} onClick={toggleExpansion}>{text.hideDetail}<ChevronUpIcon color='primary.500' /></Button>
            </Box>
          </Box>
        )}

        {(isStepAdditionalServcice || isStepSummary || isSummaryFinal) && additionalServicesCount > 0 && (
          <Box p={7} pt={0}>
            <Box>
              <Divider />
            </Box>
            <Flex dir='row' justifyContent='space-between' textStyle='labelMdBold' color='primary.500' pt={7} pb={2}>
              <Text color='grey.120' textStyle='titleMd' fontWeight='700'>
                {` +${additionalServicesCount} servicio${additionalServicesCount !== 1 ? 's' : ''} adicional${additionalServicesCount !== 1 ? 'es' : ''}`}
              </Text>
              {(isStepSummary || isSummaryFinal)  && 
              (<Button 
                w='79px' 
                variant='primary-outline' 
                color='primary.500' 
                data-testid='changeAdditionalService' 
                onClick={handleAdditionalServcice}
                disabled={isSummaryFinal}
                >
                <Text textStyle='labelLgBold' p={1}>{text.change}</Text>
              </Button>)}
          </Flex>
          </Box>
        )}
        {/* card violeta : precio final + listado de serv agregados */}
        <Box p={7} bg='tertiary.110' borderBottomEndRadius={8} borderBottomStartRadius={8}>
          <Flex dir='column' bg='tertiary.110'>
            <Box mb={2} dir='column' flex={0.7} >
              <Text color='white' textStyle='labelMdBold'>{priceByTitle}</Text>
            </Box>
          </Flex>
          <Flex dir='column' bg='tertiary.110' borderBottomRadius='xl' justifyContent='flex-end' alignItems='flex-end'  >
            <Box dir='column'>
              <Text textAlign='right' color='white' textStyle='h3'>{totalPrice}</Text>
              <Box alignItems="flex-end">
                <Text textAlign='right' color='white' textStyle='labelMdBold'> +IVA por mes</Text>
              </Box>
            </Box>
          </Flex>
          {(isStepAdditionalServcice || isStepSummary || isSummaryFinal) && !showDetails && additionalServicesCount > 0 && (
            <Flex>
              <Button data-testid='viewDetailServices' color='white' textStyle='labelSmBoldUnderline' variant='primary-text' p='0' onClick={toggleExpansionService}>{text.showDetail} <ChevronDownIcon color='white' /></Button>
            </Flex>)}

          {/* ver detalles de los serv agregados */}
          {showDetails && additionalServicesCount > 0 && (
            <Stack>
              {/* datos del plan */}
              <Box borderTop='1px solid' borderTopColor='tertiary.70' mt='24px'>
                <Flex mb={2} mt='24px'>
                  <Text color='white' textStyle='labelMdBold'>{text.plan}</Text>
                </Flex>
                <Flex color='white' justifyContent='space-between' mb={2}>
                  <Text textStyle='labelMd'>{planName}</Text>
                  <Text textStyle='labelLgBold'>{moneyFormat(planPrice, 'ARS')}</Text>
                </Flex>
              </Box>
              {/* listado de servicios agregados */}
              <Box mt={4}>
                <Flex mb={3}>
                  <Text color='white' textStyle='labelMdBold'>{text.additionalService}</Text>
                </Flex>
                {selectedOfferings?.additionalServices?.map(service => (
                  service.type === 'variable' ?
                    <Box>
                      <Flex color='white' key={service.codeDn} justifyContent='space-between' mb={1}>
                        <Text textStyle='labelMd'>{service.denomination}</Text>
                        <HStack spacing='4px'>
                          <Text textStyle='labelSm' >{text.since}</Text>
                          <Text textStyle='labelLgBold'>{moneyFormat(service.price, 'ARS')}</Text>
                        </HStack>
                      </Flex>
                      <Flex mb={3} justifyContent='space-between' alignItems='start'>
                        <Text textStyle='labelSm' color='tertiary.40' pt='2px'>{text.conditionPrice}</Text>
                        <Button 
                          alignItems='start'
                          data-testid='viewDescriptionPrice' 
                          textStyle='labelSmBoldUnderline' 
                          fontSize='12px'
                          size='xs'
                          variant='primary-text' 
                          p='0'
                          color='white'
                          onClick={() => openModalWithNote(service.note , service.denomination, service.codeDn)}
                        >
                          Ver precios
                        </Button>
                      </Flex>
                    </Box>
                    :
                    <Flex color='white' key={service.codeDn} justifyContent='space-between' mb={2}>
                      <Text textStyle='labelMd'>{service.denomination}</Text>
                      <Text textStyle='labelLgBold'>{moneyFormat(service.price, 'ARS')}</Text>
                    </Flex>
                ))}
              </Box>
              {/* Ocultar detalles */}
              <Box mt={6} color='primary.500'>
                <Button data-testid='hideDetailServices' color='white' textStyle='labelSmBoldUnderline' variant='primary-text' p='0' onClick={toggleExpansionService}>{text.hideDetail}<ChevronUpIcon color='white' /></Button>
              </Box>
            </Stack>
          )}
        </Box>
        <IBModalAction
        isOpen={isOpenModalPrice}
        onClose={onCloseModalPrice}
        buttonAction={onCloseModalPrice}
        buttonActionText='Entendido'
        title={`Precios del servicio de ${getTitle()}`}
        textBody={selectedService.note}
        iconView='info'
      />
      </Box>
  )
}

export default SummaryPlan
